import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  generateSchedule,
  listSystemType
} from '../../../lib/api/competition';

const parseAllowedDays = allowedDays => {
  if (Array.isArray(allowedDays)) {
    return allowedDays.map(Number);
  }

  if (typeof allowedDays === 'string' && allowedDays.trim()) {
    try {
      const parsed = JSON.parse(allowedDays);
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

const daysOfWeek = [
  { value: 1, label: 'Ponedjeljak' },
  { value: 2, label: 'Utorak' },
  { value: 3, label: 'Srijeda' },
  { value: 4, label: 'Četvrtak' },
  { value: 5, label: 'Petak' },
  { value: 6, label: 'Subota' },
  { value: 0, label: 'Nedjelja' }
];

export const GenerateSchedule = ({ initialData, onClose, onSave }) => {
  const [competitionSystemTypes, setCompetitionSystemTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resSystemType = await listSystemType();
        setCompetitionSystemTypes(resSystemType);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const competition = useMemo(
    () => ({
      competitions_id: initialData?.competitions_id || '',
      startDate:
        initialData?.startDate ||
        initialData?.start_date?.split('T')[0] ||
        '',
      allowedDays: parseAllowedDays(
        initialData?.allowedDays ?? initialData?.allowed_days
      ),
      competition_system_type_id:
        Number(initialData?.competition_system_type_id) || 1
    }),
    [initialData]
  );

  const handleSubmit = async event => {
    event.preventDefault();

    if (!competition.competitions_id) {
      toast.error('Nedostaje competitions_id za obradu rasporeda.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await generateSchedule(competition.competitions_id);
      toast.success(response?.message || 'Raspored je uspjesno generisan.');
      onSave?.();
      onClose?.();
    } catch (error) {
      toast.error(
        error?.body?.message ||
          error?.body?.error ||
          error?.message ||
          'Doslo je do greske prilikom obrade.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="competitions_id">
              <Form.Label>Competitions ID:</Form.Label>
              <Form.Control value={competition.competitions_id} disabled />
            </Form.Group>
          </Row>

          <hr className="my-4" />

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="startDate">
              <Form.Label>Pocetak takmicenja:</Form.Label>
              <Form.Control
                type="date"
                value={competition.startDate}
                disabled
              />
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="competition_system_type_id">
              <Form.Label>Sistem takmicenja:</Form.Label>
              <Form.Select
                value={competition.competition_system_type_id}
                disabled
              >
                {competitionSystemTypes.map(item => (
                  <option
                    key={item.competition_system_type_id}
                    value={item.competition_system_type_id}
                  >
                    {item.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} md="12" controlId="allowedDays">
              <Form.Label>Dani odigravanja:</Form.Label>
              <div className="border rounded p-3">
                <Row>
                  {daysOfWeek.map(day => (
                    <Col md="3" key={day.value} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`generate-allowedDays-${day.value}`}
                        checked={competition.allowedDays.includes(day.value)}
                        label={day.label}
                        disabled
                        readOnly
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            </Form.Group>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              type="button"
            >
              Zatvori
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Obrada...
                </>
              ) : (
                'Obrada'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

GenerateSchedule.propTypes = {
  initialData: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

export default GenerateSchedule;
